<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      In this activity, you’ll learn all about collecting fingerprints and then complete two tasks
      -so you’ll be an expert in no time!
    </p>
    <p class="mb-5">
      Fingerprints are the most commonly used type of forensic evidence and have been for over a
      hundred years. This is because they are incredibly useful in solving crimes - you’ll learn why
      shortly.
    </p>
    <p class="mb-5">
      First, listen to the strange tale of the two William Wests, which explains how fingerprinting
      came to be heavily relied on for identification purposes.
    </p>
    <p class="mb-5">Check out the video below.</p>
    <responsive-youtube-wrapper :video="videos[1].url" :image="videos[1].image" />
    <p class="mb-5">
      In this activity, you will discover how fingerprints are formed, how to gather them as
      evidence, and how to analyse them for their distinct patterns - are you ready to become the
      new Sherlock Holmes?
    </p>
    <p class="mb-5">
      Watch the video below so you can understand the basic features of a fingerprint:
    </p>
    <responsive-youtube-wrapper :video="videos[2].url" :image="videos[2].image" />

    <h4 class="mb-5">Task 1: Taking Your Own Fingerprints</h4>
    <p class="mb-5">
      Fascinatingly, each individual has unique prints – as you saw in the above video. Of course,
      all this is no good if the forensic scientist doesn’t know how to ‘read’ fingerprints.
    </p>
    <p class="mb-5">
      The first thing you’ll need to do is collect some really good fingerprints from yourself (or a
      friend).
    </p>
    <p class="mb-5">The following simple method doesn’t even require ink. All you’ll need is:</p>
    <ul class="default-list">
      <li>A pencil (a soft pencil such as 3B works best)</li>
      <li>A sheet of paper</li>
      <li>Tape</li>
      <li>Some fingers!</li>
      <li>The fingerprint recording sheet</li>
    </ul>
    <p class="mb-5">Next, watch the video below and download the Fingerprint Recording Sheet.</p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5 flex justify-center">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: How to capture your fingerprints"
          @click="showVideoPopup(videos[3].url)"
        >
          <img
            src="@/assets/images/experiences/forensic_science/fingerprint_capture_splash.jpg"
            class="mx-auto mb-5 img-responsive cursor-pointer"
            alt=""
          />
        </button>
      </div>
      <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Open the Fingerprint Recording Sheet (PDF)"
          @click="showIframePopup(popupImage)"
        >
          <img
            src="@/assets/images/experiences/forensic_science/fingerprint_recording_sheet_image.jpg"
            class="mx-auto mb-5 img-responsive cursor-pointer"
            alt=""
          />
        </button>
      </div>
    </div>

    <p class="mb-5">
      Once you’ve watched the video and feel confident with the fingerprint “lifting” technique,
      follow the process for all 10 fingers and record/place these in the correct spot on the sheet
      for each finger (yes, including thumbs!).
    </p>
    <p class="mb-5">
      Make sure you record the name of the person whose fingerprints you captured (yours or someone
      else’s), then complete the section about the number of arches, loops and whorls (this is the
      total number of each for all 10 fingers).
    </p>
    <p class="mb-5">Once you have completed this sheet, take a photo of it and upload it below.</p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Image'"
      index="1"
      filetype="media"
    />

    <p class="mb-5">
      OK, now let’s step it up a notch! We’ll now see how you go with lifting fingerprints off a
      real object - let’s give you some basic knowledge on how to do this and then you can try it
      for yourself.
    </p>
    <p class="mb-5">To get started, watch the short video below on how to dust for fingerprints.</p>
    <responsive-youtube-wrapper :video="videos[4].url" :image="videos[4].image" />
    <p class="mb-5">
      Read the following two articles to give you some background knowledge before you start your
      task (Wikihow on the left and Scientific American on the right):
    </p>
    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5 flex justify-center">
        <a href="https://www.wikihow.com/Dust-for-Fingerprints" target="_blank">
          <img
            src="@/assets/images/experiences/forensic_science/wiki_button.jpg"
            alt="Read 'How to dust for fingerprints' on wikiHow (opens in a new tab)"
            class="mx-auto mb-5 img-responsive cursor-pointer"
          />
        </a>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Open the Scientific American article 'Finding Fingerprints'"
          @click="
            showIframePopup('https://www.scientificamerican.com/article/finding-fingerprints/')
          "
        >
          <img
            src="@/assets/images/experiences/forensic_science/sci_american_article_button.jpg"
            class="mx-auto mb-5 img-responsive cursor-pointer"
            alt=""
          />
        </button>
      </div>
    </div>
    <h4 class="mb-5">Task 2: Lifting Fingerprints</h4>
    <p class="mb-5">
      This one’s a bit trickier. You are going to use the techniques outlined in the video and
      articles above.
    </p>
    <p class="mb-5">To complete this task you will need:</p>
    <ul class="default-list">
      <li>
        An item to put a fingerprint on – an old CD or DVD works best, but something like a glass is
        OK too
      </li>
      <li>A small bowl of Cornflour</li>
      <li>A light brush – a makeup brush is ideal or a very soft paintbrush will work as well</li>
      <li>Sticky tape</li>
      <li>
        A sheet of black paper (or white paper and you can colour in a black square with a text)
      </li>
    </ul>
    <p class="mb-5">Watch the following video, where you’ll learn how to do the experiment:</p>
    <p class="mb-5">
      Note: You only need to watch the first 7 minutes of the video. The task you need to complete
      is covered in this first section.
    </p>
    <responsive-youtube-wrapper :video="videos[5].url" :image="videos[5].image" />

    <p class="mb-5">
      Now, try repeating the process you see demonstrated in the video. It can take a bit of
      practice to get it right so you may need to try it a few times – practice makes perfect!
    </p>
    <p class="mb-5">Were you able to lift a good fingerprint?</p>
    <p class="mb-5">Upload the evidence of this below. You should upload 2 photos:</p>
    <p class="mb-5">
      1. The dusted surface of the item you put the fingerprint on, showing the fingerprint
    </p>
    <p class="mb-5">
      (click
      <button type="button" class="no-style inline-link" @click="showIframePopup(image2)">
        here
      </button>
      to see an example)
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="2"
      filetype="media"
    />

    <p class="mb-5">2. The paper after you have stuck the sticky taped print to it</p>
    <p class="mb-5">
      (click
      <button type="button" class="no-style inline-link" @click="showIframePopup(image3)">
        here
      </button>
      to see an example)
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="3"
      filetype="media"
    />

    <p class="mb-5">
      If you have gotten this far and completed these activities you’re a forensic expert –
      Congratulations! Watch out all you dodgy criminals...
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/forensic_science/video_1_splash.jpg'),
          url: 'https://www.youtube.com/embed/3TsZ18OUis8',
        },
        2: {
          image: require('@/assets/images/experiences/forensic_science/compare_fingerprints_splash.jpg'),
          url: 'https://www.youtube.com/embed/IrpTqKkgygA',
        },
        3: {
          image: require('@/assets/images/experiences/forensic_science/compare_fingerprints_splash.jpg'),
          url:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/forensic_science/fingerprint_capture_640.mp4', //Missing video
        },
        4: {
          image: require('@/assets/images/experiences/forensic_science/activity24.jpg'),
          url: 'https://www.youtube.com/embed/JiKYD6gQnn8',
        },
        5: {
          image: require('@/assets/images/experiences/forensic_science/lift_fingerprint_splash.jpg'),
          url: 'https://www.youtube.com/embed/SdoaLExJFTA',
        },
      },
      popupImage:
        $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
        '/content/forensic_science/fingerprint_sheet.pdf',
      image1: require('@/assets/images/experiences/fitness/HIIT-Formula-Infographic-1.jpg'),
      image2: require('@/assets/images/experiences/forensic_science/dusted_fingerprint.jpg'),
      image3: require('@/assets/images/experiences/forensic_science/lifted_print.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
